import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/Close";
import { ClipLoader } from "react-spinners";

const unassignedBadgeStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "4px",
  fontWeight: "bold",
  fontSize: "11px",
  color: "#fff",
  whiteSpace: "nowrap",
  backgroundColor: "#f57c00",
};

const fmtTime = (t) => {
  if (!t) return "—";
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? t : d.toLocaleString();
};

// AddDevicesDialog shows the pool of batteries not yet assigned to any member and
// lets the admin select one or more to assign to the member in context. Selection
// is kept in local state and persists as more pages are loaded via the cursor.
const AddDevicesDialog = ({
  open,
  onClose,
  memberID,
  memberName,
  devices,
  hasNext,
  isLoading,
  isFetchingMore,
  isAssigning,
  refreshRecommended,
  newerCount,
  doFetchUnassignedDevices,
  doFetchMoreUnassignedDevices,
  doAssignDevices,
  doResetUnassignedDevices,
}) => {
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset and load the first page whenever the dialog opens. We intentionally do
  // NOT clear on close: resetting while the dialog animates out empties the table
  // and briefly shows the "no devices" state. Stale data is cleared on next open.
  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setSearch("");
      setConfirmOpen(false);
      doResetUnassignedDevices();
      doFetchUnassignedDevices();
    }
  }, [open]);

  // Client-side search over the already-fetched devices. Matches the device id
  // (primary) and the last-reported timestamp as substrings, case-insensitively.
  const filteredDevices = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter(
      (d) =>
        String(d.device_id || "").toLowerCase().includes(q) ||
        fmtTime(d.last_reported_time).toLowerCase().includes(q)
    );
  }, [devices, search]);

  const toggle = (deviceId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };

  // Select-all operates on the currently visible (filtered) rows.
  const allSelected =
    filteredDevices.length > 0 && filteredDevices.every((d) => selected.has(d.device_id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredDevices.forEach((d) => next.delete(d.device_id));
      else filteredDevices.forEach((d) => next.add(d.device_id));
      return next;
    });
  };

  const performAssign = async () => {
    const ids = [...selected];
    if (!ids.length) return;

    setConfirmOpen(false);

    // Success/failure is reported via the global page snackbar (dispatched inside
    // doAssignDevices), which renders above this dialog.
    const { failed } = await doAssignDevices(memberID, ids);

    if (!failed.length) {
      onClose();
      return;
    }
    // Partial failure: keep the dialog open, keep the failed devices selected, and
    // refresh the list so successfully-assigned ones drop off.
    setSelected(new Set(failed.map((f) => f.deviceId)));
    doFetchUnassignedDevices();
  };

  const selectedCount = selected.size;
  const memberLabel = memberName ? memberName : "this member";

  return (
    <Dialog maxWidth="md" fullWidth onClose={onClose} open={open}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "18px",
            fontWeight: "bold",
            padding: "10px 16px",
            backgroundColor: "#202124",
            color: "white",
            letterSpacing: "1px",
          }}
        >
          <span>Add Devices to Member</span>
          <CloseIcon style={{ cursor: "pointer" }} onClick={onClose} />
        </div>

        <div style={{ padding: "12px 16px" }}>
          <div style={{ fontSize: "13px", color: "#555", marginBottom: "10px" }}>
            These devices are <span style={unassignedBadgeStyle}>Unassigned</span> — not yet
            linked to any member. Select the ones to assign to this member.
          </div>

          {refreshRecommended && newerCount > 0 && (
            <div
              style={{
                fontSize: "12px",
                color: "#e65100",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {newerCount} newer device(s) reported since this list loaded.
              <Button size="small" onClick={() => doFetchUnassignedDevices()}>
                Refresh
              </Button>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              marginBottom: "6px",
            }}
          >
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by Device ID…"
              style={{
                flex: 1,
                maxWidth: "320px",
                padding: "6px 10px",
                fontSize: "13px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
            <span style={{ fontSize: "12px", color: "#666", whiteSpace: "nowrap" }}>
              {search.trim()
                ? `${filteredDevices.length} of ${devices.length} fetched`
                : `${devices.length} fetched`}
              {hasNext ? " (more available)" : ""}
            </span>
          </div>

          <div style={{ maxHeight: "420px", overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ background: "#f5f5f5", position: "sticky", top: 0 }}>
                  <th style={{ padding: "8px", width: "40px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={!filteredDevices.length}
                      aria-label="Select all"
                    />
                  </th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Device ID</th>
                  <th style={{ padding: "8px", textAlign: "left" }}>Last Reported</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map((d) => (
                  <tr
                    key={d.device_id}
                    style={{ borderTop: "1px solid #eee", cursor: "pointer" }}
                    onClick={() => toggle(d.device_id)}
                  >
                    <td style={{ padding: "8px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selected.has(d.device_id)}
                        onChange={() => toggle(d.device_id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${d.device_id}`}
                      />
                    </td>
                    <td style={{ padding: "8px", fontFamily: "monospace" }}>{d.device_id}</td>
                    <td style={{ padding: "8px" }}>{fmtTime(d.last_reported_time)}</td>
                  </tr>
                ))}
                {!filteredDevices.length && !isLoading && (
                  <tr>
                    <td colSpan={3} style={{ padding: "24px", textAlign: "center", color: "#888" }}>
                      {search.trim() ? (
                        <>
                          No devices match “{search.trim()}”.
                          {hasNext && (
                            <div style={{ marginTop: "6px", color: "#e65100" }}>
                              Consider loading more to find the searched device id.
                            </div>
                          )}
                        </>
                      ) : (
                        "No unassigned devices found."
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {isLoading && (
              <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                <ClipLoader size={28} color="#3f51b5" />
              </div>
            )}
          </div>

          {hasNext && (
            <div style={{ textAlign: "center", marginTop: "10px" }}>
              <Button
                size="small"
                variant="outlined"
                disabled={isFetchingMore}
                onClick={() => doFetchMoreUnassignedDevices()}
              >
                {isFetchingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 16px",
            borderTop: "2px solid #ddd",
          }}
        >
          <span style={{ fontSize: "13px", color: "#555" }}>
            {selectedCount} selected
          </span>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Button color="secondary" onClick={onClose} disabled={isAssigning}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={selectedCount === 0 || isAssigning}
              onClick={() => setConfirmOpen(true)}
            >
              {isAssigning
                ? "Assigning…"
                : `Assign${selectedCount ? ` (${selectedCount})` : ""}`}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Please Confirm</DialogTitle>
        <DialogContent dividers>
          You are about to assign {selectedCount} device{selectedCount === 1 ? "" : "s"} to{" "}
          {memberLabel}, press confirm to continue.
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setConfirmOpen(false)} disabled={isAssigning}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={performAssign}
            disabled={isAssigning}
          >
            {isAssigning ? "Assigning…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

AddDevicesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  memberID: PropTypes.any,
  memberName: PropTypes.string,
  devices: PropTypes.array,
  hasNext: PropTypes.bool,
  isLoading: PropTypes.bool,
  isFetchingMore: PropTypes.bool,
  isAssigning: PropTypes.bool,
  refreshRecommended: PropTypes.bool,
  newerCount: PropTypes.number,
  doFetchUnassignedDevices: PropTypes.func.isRequired,
  doFetchMoreUnassignedDevices: PropTypes.func.isRequired,
  doAssignDevices: PropTypes.func.isRequired,
  doResetUnassignedDevices: PropTypes.func.isRequired,
};

AddDevicesDialog.defaultProps = {
  devices: [],
};

export default AddDevicesDialog;
